import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ReloadOutlined, EditOutlined, WarningOutlined, PlusOutlined, CopyOutlined, CalculatorOutlined } from '@ant-design/icons';
import { Modal, Tag, Select, Input, InputNumber, Spin, Tabs } from 'antd';
import {
  adminService,
  PricingItem,
  PricingItemUpdate,
} from '../../services/admin.service';
import { useNotification } from '../../hooks/useNotification';
import EmptyState from '../../components/shared/EmptyState/EmptyState';

const { TextArea } = Input;

/** priceAmount se guarda en centavos; se muestra en unidades de la moneda. */
const centsToUnits = (cents: number): number => cents / 100;
const unitsToCents = (units: number): number => Math.round(units * 100);

/** Precio por moneda: usa el mapa `prices` y cae al legacy priceAmount/currency. */
const priceInCurrency = (item: PricingItem, currency: 'MXN' | 'USD'): number | undefined => {
  if (item.prices && item.prices[currency] !== undefined) return item.prices[currency];
  if (item.currency === currency) return item.priceAmount;
  return undefined;
};

const formatPrice = (item: PricingItem): string => {
  const interval =
    item.billingInterval === 'MONTH' ? '/mes'
      : item.billingInterval === 'YEAR' ? '/año'
        : '';
  const parts: string[] = [];
  const mxn = priceInCurrency(item, 'MXN');
  const usd = priceInCurrency(item, 'USD');
  if (mxn !== undefined) {
    parts.push(`$${centsToUnits(mxn).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN${interval}`);
  }
  if (usd !== undefined) {
    parts.push(`$${centsToUnits(usd).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD${interval}`);
  }
  return parts.join(' · ') || '—';
};

interface PricingFormState {
  displayName: string;
  description: string;
  priceMXN: number | null;
  priceUSD: number | null;
  status: 'ACTIVE' | 'INACTIVE';
  billingInterval: string;
  billingModel: string;
  compatiblePlans: string;
}

const AdminPricingPage: React.FC = () => {
  const [plans, setPlans] = useState<PricingItem[]>([]);
  const [addons, setAddons] = useState<PricingItem[]>([]);
  const [fxRate, setFxRate] = useState<number | null>(null);
  const [fxDraft, setFxDraft] = useState<number | null>(null);
  const [isSavingFx, setIsSavingFx] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [familyFilter, setFamilyFilter] = useState<string>('all');

  const [editingItem, setEditingItem] = useState<PricingItem | null>(null);
  const [form, setForm] = useState<PricingFormState | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Creación: variante (duplicando), plan desde cero y add-on
  const [variantSource, setVariantSource] = useState<PricingItem | null>(null);
  const [variantForm, setVariantForm] = useState({ variantCode: '', displayName: '', usd: null as number | null, mxn: null as number | null });
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({ typeFamily: '', osType: 'GENERIC', osFamily: '', planCode: '', variantCode: 'STANDARD', planName: '', billingInterval: 'MONTH', billingModel: 'FIXED', usd: null as number | null, mxn: null as number | null });
  const [creatingAddon, setCreatingAddon] = useState(false);
  const [addonForm, setAddonForm] = useState({ typeFamily: '', osType: 'LINUX', addonCode: '', addonName: '', description: '', compatiblePlans: '', usd: null as number | null, mxn: null as number | null });

  const { success: showSuccess, error: showError } = useNotification();

  const loadPricing = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const response = await adminService.getPricing();
    if (!response.success) {
      setError(response.error || 'Error al cargar el catálogo de precios');
      showError(`Error al cargar precios: ${response.error}`);
      setPlans([]);
      setAddons([]);
    } else {
      setPlans(response.plans);
      setAddons(response.addons);
      setFxRate(response.config?.usdToMxn ?? null);
      setFxDraft(response.config?.usdToMxn ?? null);
    }
    setIsLoading(false);
  }, [showError]);

  const handleSaveFx = async () => {
    if (fxDraft === null || fxDraft <= 0) {
      showError('El tipo de cambio debe ser mayor a 0');
      return;
    }
    setIsSavingFx(true);
    try {
      const response = await adminService.updatePricingConfig(fxDraft);
      if (!response.success) {
        showError(`Error al guardar tipo de cambio: ${response.error}`);
        return;
      }
      setFxRate(fxDraft);
      showSuccess(`Tipo de cambio de referencia: 1 USD = $${fxDraft} MXN`);
    } finally {
      setIsSavingFx(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const families = useMemo(
    () =>
      Array.from(
        new Set([...plans, ...addons].map((i) => i.typeFamily).filter(Boolean)),
      ).sort(),
    [plans, addons],
  );

  const visiblePlans = useMemo(
    () => (familyFilter === 'all' ? plans : plans.filter((p) => p.typeFamily === familyFilter)),
    [plans, familyFilter],
  );
  const visibleAddons = useMemo(
    () => (familyFilter === 'all' ? addons : addons.filter((a) => a.typeFamily === familyFilter)),
    [addons, familyFilter],
  );

  const openEdit = (item: PricingItem) => {
    const mxn = priceInCurrency(item, 'MXN');
    const usd = priceInCurrency(item, 'USD');
    setEditingItem(item);
    setForm({
      displayName: item.displayName || item.planName || item.addonName || '',
      description: item.description || '',
      priceMXN: mxn !== undefined ? centsToUnits(mxn) : null,
      priceUSD: usd !== undefined ? centsToUnits(usd) : null,
      status: item.status,
      billingInterval: item.billingInterval,
      billingModel: item.billingModel,
      compatiblePlans: (item.compatiblePlans || []).join(', '),
    });
  };

  const closeModal = () => {
    setEditingItem(null);
    setForm(null);
  };

  const handleSave = async () => {
    if (!editingItem || !form) return;
    const baseCurrency = editingItem.currency === 'USD' ? 'USD' : 'MXN';
    const basePrice = baseCurrency === 'USD' ? form.priceUSD : form.priceMXN;
    if (basePrice === null || basePrice < 0) {
      showError(`El precio en ${baseCurrency} (moneda base del item) es obligatorio y debe ser >= 0`);
      return;
    }

    const prices: Record<string, number> = {};
    if (form.priceMXN !== null && form.priceMXN >= 0) prices.MXN = unitsToCents(form.priceMXN);
    if (form.priceUSD !== null && form.priceUSD >= 0) prices.USD = unitsToCents(form.priceUSD);

    const isAddon = editingItem.entityType === 'ADDON_PRICE';
    const update: PricingItemUpdate = {
      PK: editingItem.PK,
      SK: editingItem.SK,
      expectedVersion: editingItem.version,
      // priceAmount (legacy, lo que lee platform-orders hoy) se mantiene
      // sincronizado con el precio en la moneda base del item
      priceAmount: unitsToCents(basePrice),
      prices,
      status: form.status,
      billingInterval: form.billingInterval,
      billingModel: form.billingModel,
      description: form.description,
    };
    if (isAddon) {
      update.addonName = form.displayName;
      update.compatiblePlans = form.compatiblePlans
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
    } else {
      update.displayName = form.displayName;
    }

    setIsSaving(true);
    try {
      const response = await adminService.updatePricingItem(update);
      if (!response.success) {
        showError(`Error al guardar: ${response.error}`);
        return;
      }
      showSuccess('Precio actualizado');
      closeModal();
      loadPricing();
    } finally {
      setIsSaving(false);
    }
  };

  const buildPricesFromForm = (usd: number | null, mxn: number | null): Record<string, number> => {
    const prices: Record<string, number> = { MXN: unitsToCents(mxn!) };
    if (usd !== null && usd >= 0) prices.USD = unitsToCents(usd);
    return prices;
  };

  const handleCreateVariant = async () => {
    if (!variantSource) return;
    if (!variantForm.variantCode.trim()) { showError('El código de la variante es obligatorio'); return; }
    if (variantForm.mxn === null || variantForm.mxn < 0) { showError('El precio MXN es obligatorio'); return; }
    setIsSaving(true);
    try {
      const response = await adminService.createPricingVariant({
        sourcePK: variantSource.PK,
        sourceSK: variantSource.SK,
        variantCode: variantForm.variantCode.trim().toUpperCase(),
        displayName: variantForm.displayName || undefined,
        priceAmount: unitsToCents(variantForm.mxn),
        prices: buildPricesFromForm(variantForm.usd, variantForm.mxn),
      });
      if (!response.success) { showError(`Error al crear variante: ${response.error}`); return; }
      showSuccess('Variante creada (inactiva)');
      setVariantSource(null);
      loadPricing();
    } finally { setIsSaving(false); }
  };

  const handleCreatePlan = async () => {
    if (!planForm.typeFamily.trim() || !planForm.planCode.trim() || !planForm.planName.trim()) {
      showError('Familia, código y nombre del plan son obligatorios'); return;
    }
    if (planForm.mxn === null || planForm.mxn < 0) { showError('El precio MXN es obligatorio'); return; }
    setIsSaving(true);
    try {
      const response = await adminService.createPricingPlan({
        typeFamily: planForm.typeFamily.trim().toUpperCase(),
        planCode: planForm.planCode.trim().toUpperCase(),
        planName: planForm.planName.trim(),
        osType: planForm.osType.trim().toUpperCase() || 'GENERIC',
        osFamily: planForm.osFamily.trim().toUpperCase() || undefined,
        variantCode: planForm.variantCode.trim().toUpperCase() || undefined,
        billingInterval: planForm.billingInterval,
        billingModel: planForm.billingModel,
        priceAmount: unitsToCents(planForm.mxn),
        prices: buildPricesFromForm(planForm.usd, planForm.mxn),
      });
      if (!response.success) { showError(`Error al crear plan: ${response.error}`); return; }
      showSuccess('Plan creado (inactivo)');
      setCreatingPlan(false);
      loadPricing();
    } finally { setIsSaving(false); }
  };

  const handleCreateAddon = async () => {
    if (!addonForm.typeFamily.trim() || !addonForm.addonCode.trim() || !addonForm.addonName.trim()) {
      showError('Familia, código y nombre del add-on son obligatorios'); return;
    }
    if (addonForm.mxn === null || addonForm.mxn < 0) { showError('El precio MXN es obligatorio'); return; }
    setIsSaving(true);
    try {
      const response = await adminService.createPricingAddon({
        typeFamily: addonForm.typeFamily.trim().toUpperCase(),
        osType: addonForm.osType.trim().toUpperCase() || 'LINUX',
        addonCode: addonForm.addonCode.trim().toUpperCase(),
        addonName: addonForm.addonName.trim(),
        description: addonForm.description || undefined,
        priceAmount: unitsToCents(addonForm.mxn),
        prices: buildPricesFromForm(addonForm.usd, addonForm.mxn),
        compatiblePlans: addonForm.compatiblePlans.split(',').map((p) => p.trim().toUpperCase()).filter(Boolean),
      });
      if (!response.success) { showError(`Error al crear add-on: ${response.error}`); return; }
      showSuccess('Add-on creado (inactivo)');
      setCreatingAddon(false);
      loadPricing();
    } finally { setIsSaving(false); }
  };

  const renderTable = (items: PricingItem[], isAddonTable: boolean) => {
    if (items.length === 0) {
      return (
        <EmptyState
          title={isAddonTable ? 'Sin add-ons' : 'Sin planes'}
          description="No hay items que coincidan con el filtro."
        />
      );
    }
    const headers = isAddonTable
      ? ['Add-on', 'Familia', 'Precio', 'Compatible con', 'Estado', '']
      : ['Plan', 'Familia', 'Variante', 'Precio', 'Cobro', 'Estado', ''];
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {headers.map((h, i) => (
                  <th
                    key={`${h}-${i}`}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {items.map((item) => (
                <tr key={`${item.PK}|${item.SK}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {isAddonTable
                        ? item.addonName || item.addonCode
                        : item.displayName || item.planName || item.planCode}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {isAddonTable ? item.addonCode : item.planCode}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.typeFamily}</td>
                  {!isAddonTable && (
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {item.variantName || item.variantCode || '—'}
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {formatPrice(item)}
                  </td>
                  {isAddonTable ? (
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {(item.compatiblePlans || []).join(', ') || '—'}
                    </td>
                  ) : (
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {item.billingModel}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <Tag color={item.status === 'ACTIVE' ? 'green' : 'default'}>{item.status}</Tag>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(item)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    >
                      <EditOutlined /> Editar
                    </button>
                    {!isAddonTable && (
                      <button
                        onClick={() => {
                          setVariantForm({ variantCode: '', displayName: '', usd: null, mxn: null });
                          setVariantSource(item);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      >
                        <CopyOutlined /> Nueva variante
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Planes y precios</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Catálogo de precios (KubiyPricingCatalog-prod) — planes por familia y add-ons
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCreatingPlan(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <PlusOutlined /> Crear plan
            </button>
            <button
              onClick={() => setCreatingAddon(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <PlusOutlined /> Crear add-on
            </button>
            <button
              onClick={loadPricing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ReloadOutlined /> Actualizar
            </button>
          </div>
        </div>

        {/* Aviso */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6 flex items-start gap-3">
          <WarningOutlined className="text-amber-500 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Estos precios se usan en producción al calcular órdenes nuevas
            (platform-orders). Un cambio aquí afecta el costo que ve el cliente
            de inmediato. Cada cambio queda registrado en auditoría.
          </p>
        </div>

        {/* Tipo de cambio de referencia */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Tipo de cambio de referencia:
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">1 USD =</span>
          <InputNumber
            min={0}
            precision={2}
            value={fxDraft}
            onChange={setFxDraft}
            placeholder="18.50"
            style={{ width: 110 }}
          />
          <span className="text-sm text-gray-500 dark:text-gray-400">MXN</span>
          <button
            onClick={handleSaveFx}
            disabled={isSavingFx || fxDraft === fxRate}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm rounded-lg transition-colors"
          >
            Guardar
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
            Solo sugiere el MXN al capturar precios; no cambia ningún precio guardado.
          </span>
        </div>

        {/* Filtro por familia */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex items-center gap-4">
          <span className="text-sm text-gray-700 dark:text-gray-300">Familia:</span>
          <select
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">Todas</option>
            {families.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
            {visiblePlans.length} planes · {visibleAddons.length} add-ons
          </span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Spin size="large" />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : (
          <Tabs
            defaultActiveKey="plans"
            items={[
              { key: 'plans', label: `Planes (${visiblePlans.length})`, children: renderTable(visiblePlans, false) },
              { key: 'addons', label: `Add-ons (${visibleAddons.length})`, children: renderTable(visibleAddons, true) },
            ]}
          />
        )}

        {/* Modal de edición */}
        <Modal
          title={`Editar precio — ${editingItem?.displayName || editingItem?.planName || editingItem?.addonName || ''}`}
          open={editingItem !== null}
          onCancel={closeModal}
          onOk={handleSave}
          okText="Guardar cambios"
          cancelText="Cancelar"
          confirmLoading={isSaving}
          destroyOnHidden
        >
          {editingItem && form && (
            <div className="pt-4">
              <div className="mb-4 text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                {editingItem.PK} · {editingItem.SK}
              </div>
              <div className="mb-4">
                <label className={labelClass}>Nombre visible</label>
                <Input
                  value={form.displayName}
                  onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className={labelClass}>Descripción</label>
                <TextArea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className={labelClass}>
                    Precio MXN{editingItem.currency === 'MXN' ? ' (base)' : ''}
                  </label>
                  <InputNumber
                    className="w-full"
                    min={0}
                    step={1}
                    precision={2}
                    value={form.priceMXN}
                    onChange={(value) => setForm({ ...form, priceMXN: value })}
                    placeholder="—"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Precio USD{editingItem.currency === 'USD' ? ' (base)' : ''}
                  </label>
                  <InputNumber
                    className="w-full"
                    min={0}
                    step={1}
                    precision={2}
                    value={form.priceUSD}
                    onChange={(value) => setForm({ ...form, priceUSD: value })}
                    placeholder="—"
                  />
                </div>
                <div>
                  <label className={labelClass}>Estado</label>
                  <Select
                    className="w-full"
                    value={form.status}
                    onChange={(value) => setForm({ ...form, status: value })}
                    options={[
                      { value: 'ACTIVE', label: 'ACTIVE' },
                      { value: 'INACTIVE', label: 'INACTIVE' },
                    ]}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className={labelClass}>Intervalo de cobro</label>
                  <Select
                    className="w-full"
                    value={form.billingInterval}
                    onChange={(value) => setForm({ ...form, billingInterval: value })}
                    options={['MONTH', 'YEAR', 'ONE_TIME'].map((v) => ({ value: v, label: v }))}
                  />
                </div>
                <div>
                  <label className={labelClass}>Modelo de cobro</label>
                  <Select
                    className="w-full"
                    value={form.billingModel}
                    onChange={(value) => setForm({ ...form, billingModel: value })}
                    options={['FIXED', 'PER_UNIT', 'FIXED_WITH_OVERAGE', 'TIERED'].map((v) => ({ value: v, label: v }))}
                  />
                </div>
              </div>
              {editingItem.entityType === 'ADDON_PRICE' && (
                <div className="mb-4">
                  <label className={labelClass}>Planes compatibles (separados por coma)</label>
                  <Input
                    value={form.compatiblePlans}
                    onChange={(e) => setForm({ ...form, compatiblePlans: e.target.value })}
                    placeholder="KY_EDGE, KY_CORE, KY_PULSE"
                  />
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Modal: nueva variante (duplicando un plan) */}
        <Modal
          title={`Nueva variante de ${variantSource?.displayName || variantSource?.planName || variantSource?.planCode || ''} (${variantSource?.variantCode || 'base'})`}
          open={variantSource !== null}
          onCancel={() => setVariantSource(null)}
          onOk={handleCreateVariant}
          okText="Crear variante (inactiva)"
          cancelText="Cancelar"
          confirmLoading={isSaving}
          destroyOnHidden
        >
          <div className="pt-4">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>Código de la variante</label>
                <Input
                  value={variantForm.variantCode}
                  onChange={(e) => setVariantForm({ ...variantForm, variantCode: e.target.value })}
                  placeholder="ej. PRO, MAX2"
                />
              </div>
              <div>
                <label className={labelClass}>Nombre visible (opcional)</label>
                <Input
                  value={variantForm.displayName}
                  onChange={(e) => setVariantForm({ ...variantForm, displayName: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className={labelClass}>Precio USD (maestro)</label>
                <InputNumber className="w-full" min={0} precision={2} value={variantForm.usd}
                  onChange={(value) => setVariantForm({ ...variantForm, usd: value })} />
              </div>
              <div>
                <label className={labelClass}>Precio MXN (congelado)</label>
                <div className="flex gap-2">
                  <InputNumber className="flex-1" min={0} precision={2} value={variantForm.mxn}
                    onChange={(value) => setVariantForm({ ...variantForm, mxn: value })} />
                  <button
                    onClick={() => { if (variantForm.usd !== null && fxRate) setVariantForm({ ...variantForm, mxn: Math.round(variantForm.usd * fxRate) }); }}
                    disabled={!fxRate || variantForm.usd === null}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-40"
                  >
                    <CalculatorOutlined />
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Copia toda la configuración del plan origen; solo cambian código, nombre y precios.
            </p>
          </div>
        </Modal>

        {/* Modal: crear plan desde cero */}
        <Modal
          title="Nuevo plan desde cero"
          open={creatingPlan}
          onCancel={() => setCreatingPlan(false)}
          onOk={handleCreatePlan}
          okText="Crear plan (inactivo)"
          cancelText="Cancelar"
          confirmLoading={isSaving}
          destroyOnHidden
        >
          <div className="pt-4">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <label className={labelClass}>Familia</label>
                <Input value={planForm.typeFamily}
                  onChange={(e) => setPlanForm({ ...planForm, typeFamily: e.target.value })}
                  placeholder={families.join(' / ') || 'APP_DEPLOY'} />
              </div>
              <div>
                <label className={labelClass}>OS type</label>
                <Input value={planForm.osType}
                  onChange={(e) => setPlanForm({ ...planForm, osType: e.target.value })}
                  placeholder="GENERIC / LINUX" />
              </div>
              <div>
                <label className={labelClass}>OS family</label>
                <Input value={planForm.osFamily}
                  onChange={(e) => setPlanForm({ ...planForm, osFamily: e.target.value })}
                  placeholder="SERVER / vacío" />
              </div>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-4">
              Estos tres valores deben coincidir con la consulta de la pantalla del
              cliente (APP_DEPLOY usa GENERIC + SERVER). Cópialos de un plan
              existente de la misma familia si hay duda.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>Código del plan</label>
                <Input value={planForm.planCode}
                  onChange={(e) => setPlanForm({ ...planForm, planCode: e.target.value })}
                  placeholder="ej. KY_CORE" />
              </div>
              <div>
                <label className={labelClass}>Código de variante</label>
                <Input value={planForm.variantCode}
                  onChange={(e) => setPlanForm({ ...planForm, variantCode: e.target.value })}
                  placeholder="STANDARD" />
              </div>
            </div>
            <div className="mb-4">
              <label className={labelClass}>Nombre del plan</label>
              <Input value={planForm.planName}
                onChange={(e) => setPlanForm({ ...planForm, planName: e.target.value })}
                placeholder="ej. Kubiy Core" />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>Intervalo de cobro</label>
                <Select className="w-full" value={planForm.billingInterval}
                  onChange={(value) => setPlanForm({ ...planForm, billingInterval: value })}
                  options={['MONTH', 'YEAR', 'ONE_TIME'].map((v) => ({ value: v, label: v }))} />
              </div>
              <div>
                <label className={labelClass}>Modelo de cobro</label>
                <Select className="w-full" value={planForm.billingModel}
                  onChange={(value) => setPlanForm({ ...planForm, billingModel: value })}
                  options={['FIXED', 'PER_UNIT', 'FIXED_WITH_OVERAGE', 'TIERED'].map((v) => ({ value: v, label: v }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className={labelClass}>Precio USD (maestro)</label>
                <InputNumber className="w-full" min={0} precision={2} value={planForm.usd}
                  onChange={(value) => setPlanForm({ ...planForm, usd: value })} />
              </div>
              <div>
                <label className={labelClass}>Precio MXN (congelado)</label>
                <div className="flex gap-2">
                  <InputNumber className="flex-1" min={0} precision={2} value={planForm.mxn}
                    onChange={(value) => setPlanForm({ ...planForm, mxn: value })} />
                  <button
                    onClick={() => { if (planForm.usd !== null && fxRate) setPlanForm({ ...planForm, mxn: Math.round(planForm.usd * fxRate) }); }}
                    disabled={!fxRate || planForm.usd === null}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-40"
                  >
                    <CalculatorOutlined />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {/* Modal: crear add-on */}
        <Modal
          title="Nuevo add-on"
          open={creatingAddon}
          onCancel={() => setCreatingAddon(false)}
          onOk={handleCreateAddon}
          okText="Crear add-on (inactivo)"
          cancelText="Cancelar"
          confirmLoading={isSaving}
          destroyOnHidden
        >
          <div className="pt-4">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>Familia</label>
                <Input value={addonForm.typeFamily}
                  onChange={(e) => setAddonForm({ ...addonForm, typeFamily: e.target.value })}
                  placeholder={families.join(' / ') || 'APP_DEPLOY'} />
              </div>
              <div>
                <label className={labelClass}>OS type</label>
                <Input value={addonForm.osType}
                  onChange={(e) => setAddonForm({ ...addonForm, osType: e.target.value })}
                  placeholder="LINUX" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>Código</label>
                <Input value={addonForm.addonCode}
                  onChange={(e) => setAddonForm({ ...addonForm, addonCode: e.target.value })}
                  placeholder="ej. BACKUP_WEEKLY" />
              </div>
              <div>
                <label className={labelClass}>Nombre</label>
                <Input value={addonForm.addonName}
                  onChange={(e) => setAddonForm({ ...addonForm, addonName: e.target.value })}
                  placeholder="ej. Backup semanal" />
              </div>
            </div>
            <div className="mb-4">
              <label className={labelClass}>Descripción (opcional)</label>
              <Input value={addonForm.description}
                onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })} />
            </div>
            <div className="mb-4">
              <label className={labelClass}>Planes compatibles (separados por coma; vacío = todos)</label>
              <Input value={addonForm.compatiblePlans}
                onChange={(e) => setAddonForm({ ...addonForm, compatiblePlans: e.target.value })}
                placeholder="KY_EDGE, KY_CORE" />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className={labelClass}>Precio USD (maestro)</label>
                <InputNumber className="w-full" min={0} precision={2} value={addonForm.usd}
                  onChange={(value) => setAddonForm({ ...addonForm, usd: value })} />
              </div>
              <div>
                <label className={labelClass}>Precio MXN (congelado)</label>
                <div className="flex gap-2">
                  <InputNumber className="flex-1" min={0} precision={2} value={addonForm.mxn}
                    onChange={(value) => setAddonForm({ ...addonForm, mxn: value })} />
                  <button
                    onClick={() => { if (addonForm.usd !== null && fxRate) setAddonForm({ ...addonForm, mxn: Math.round(addonForm.usd * fxRate) }); }}
                    disabled={!fxRate || addonForm.usd === null}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-40"
                  >
                    <CalculatorOutlined />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AdminPricingPage;
